import { useEffect, useRef, useState } from "react";

/**
 * Observes an element's size and derives a value from it via `measure`. The
 * value is recomputed on mount (with no entry) and whenever the element
 * resizes (with the observed entry). Attach the returned `ref` to the element
 * to observe.
 */
export const useResizeObserver = <V, T extends HTMLElement = HTMLDivElement>(
  measure: (element: T, entry?: ResizeObserverEntry) => V,
  initialValue: V
) => {
  const ref = useRef<T | null>(null);
  const measureRef = useRef(measure);
  measureRef.current = measure;
  const [value, setValue] = useState<V>(initialValue);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setValue(measureRef.current(element, entry));
    });
    observer.observe(element);
    setValue(measureRef.current(element));

    return () => observer.disconnect();
  }, []);

  return { ref, value };
};
